// Angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// CC-Common imports
import { DxWidgetUtils, IconConstants, Utils } from 'cc-common';

// DevExtream imports
import { DxDataGridComponent, DxPopupComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';
import { Column } from 'devextreme/ui/data_grid';

// Constant imports
import { ActionConstants } from '../../../../_constants/action-constants';
import { CiraHelpKeyConstants } from '../../../../_constants/cira-help-key-constants';
import { MessageInterval } from '../../../../_constants/messages-intervals';

// Service imports
import { CiraHelpService } from '../../../../_services/shared-services/cira-help.service';
import { HelperService } from '../../../../_services/shared-services/helper.service';
import { ServerErrorService } from '../../../../_services/shared-services/server-error.service';
import { VendorContractService } from '../../_services/vendor-contracts.service';

// Model imports
import { VendorContractResultDto } from '../../../shared-vendor-management-module/_models/vendor-contract-result-dto';
import { ActionMenuEvent, ActionMenuInfo } from '../../../../shared-module/_models/action-menu-info';
import { CommunityVendorBaseParametersDto } from '../../../shared-vendor-management-module/_models/community-vendor-base-parameters-dto';
import { ManageVendorContractResultDto } from '../../../shared-vendor-management-module/_models/manage-vendor-contract-result-dto';
import { DownloadVendorContractDocumentParameterDto } from '../../_models/download-vendor-contract-document-parameter-dto';
import { DeleteVendorContractParameterDto } from '../../_models/delete-vendor-contract-parameter-dto';
import { VendorContractBaseParameterDto } from '../../../shared-vendor-management-module/_models/vendor-contract-base-parameter-dto';
import { VendorContractHistoryResultDto } from '../../_models/vendor-contract-history-result-dto';
import { VendorManagementSharedService } from '../../_services/vendor-management-shared.service';
import { StringFormatConstants } from '../../../../_constants/string-format-constants';
import { WFVendorContractRawDto } from '../../_models/wf-vendor-contract-raw-dto';

@Component({
    selector: 'app-manage-contracts',
    templateUrl: './manage-contracts.component.html',
    styleUrls: ['./manage-contracts.component.scss']
})
export class ManageContractsComponent implements OnInit {
    @ViewChild('manageContractPopup', { static: false }) manageContractPopup: DxPopupComponent;
    @ViewChild('contractGrid', { static: false }) contractGrid: DxDataGridComponent;

    @Input() vendorName: string;
    @Input() communityVendorId: number;
    @Input() contextId: number;
    @Input() workflowId: number = null;
    @Input() vendorCode: string;
    @Input() communityId: number;

    @Output() onClose = new EventEmitter<void>();
    @Output() onSaveWorkflowVendorContractClick = new EventEmitter<Array<WFVendorContractRawDto>>();

    visible = false;
    popupTitle: string;
    helpLink: string;

    actionMenuItems: Array<ActionMenuInfo>;
    manageContracteData: VendorContractResultDto[] = [];
    selectedContract: VendorContractResultDto;
    allowAddVendorContract: boolean;
    allowActions: boolean;
    gridRowIndex: number;
    showDescription = false;
    showContractEditor = false;
    showContractDocument = false;
    documentTitle: string;

    showHistory = false;
    contractHistoryData: VendorContractHistoryResultDto[] = [];
    contractHistoryColumns: Column[] = [];

    dateTimeFormat: string;
    loadingVisible = false;

    workflowVendorContracts: Array<WFVendorContractRawDto> = [];
    selectedVendorContracts: Array<number> = [];

    iconDelete = IconConstants.DELETE;
    iconEdit = IconConstants.EDIT_NEW;
    iconClose = IconConstants.CLOSE;
    iconHistory = IconConstants.HISTORY;
    iconAdd = IconConstants.ADD;
    iconCheck = IconConstants.CHECK;
    iconCross = IconConstants.CROSS;
    iconPdf = IconConstants.PDF_FILE;
    iconHelp = IconConstants.HELP;
    iconFolder = IconConstants.FOLDER_OPEN;
    iconSave = IconConstants.SAVE;

    constructor(
        private readonly ciraHelpService: CiraHelpService,
        private readonly helperService: HelperService,
        private readonly errorService: ServerErrorService,
        private readonly vendorContractService: VendorContractService,
        private readonly vendorManagementSharedService: VendorManagementSharedService
    ) {
        this.helpLink = this.ciraHelpService.getHelpLinkByKey(CiraHelpKeyConstants.contract);
    }

    ngOnInit() {
        this.dateTimeFormat = StringFormatConstants.dateTimeFormat;
        if (Utils.notNullAndDefined(this.vendorName)) {
            this.popupTitle = this.vendorName;
            if (Utils.notNullAndDefined(this.workflowId)) {
                this.popupTitle += ` Invoice #${this.workflowId}`;
            }
        }
        this.show();
        this.buildActionItems();
    }

    onShown(): void {
        DxWidgetUtils.instantRepaint(this.manageContractPopup);
    }

    show(): void {
        this.visible = true;
        this.populateManageContract();
    }

    onHidden(): void {
        this.visible = false;
        this.onClose.emit();
    }

    onActionClick(event: ActionMenuEvent<any>): void {
        const action = event.action;
        this.selectedContract = event.record;
        if (Utils.equalsIgnoreCase(action, ActionConstants.EDIT)) {
            this.showContractEditor = true;
        } else if (Utils.equalsIgnoreCase(action, ActionConstants.DELETE)) {
            const dialog = this.helperService.getAgreementDialog(
                `Confirmation`,
                `This will permanently delete all documents of this contract. Are you sure you want to delete this contract?`,
                `Delete All Documents`,
                `Delete Contract`,
                `Cancel`,
                'danger',
                'danger',
                'default'
            );
            dialog.show().then((chioce) => {
                if (Utils.isTrue(chioce) || Utils.isFalse(chioce)) {
                    this.deleteContract(chioce);
                }
            });
        }
    }

    toggleDescription(row: any): void {
        if (
            Utils.notNullAndDefined(row) &&
            Utils.isValidNumber(this.gridRowIndex) &&
            Utils.equals(this.gridRowIndex, row.rowIndex)
        ) {
            this.showDescription = false;
            this.gridRowIndex = null;
        } else {
            this.gridRowIndex = row.rowIndex;
            this.showDescription = true;
        }
    }

    onDocumentClick(data: VendorContractResultDto): void {
        if (Utils.notNullAndDefined(data)) {
            const file = data.documents[0];
            const param: DownloadVendorContractDocumentParameterDto = new DownloadVendorContractDocumentParameterDto();
            param.communityId = this.contextId;
            param.communityVendorId = this.communityVendorId;
            param.fileId = file.fileId;
            param.vendorContractId = data.vendorContractId;
            param.wfJobId = this.workflowId;
            param.communityId = this.communityId;
            param.vendorCode = this.vendorCode;
            param.clearCache = true;
            this.loadingVisible = true;
            this.vendorContractService.downloadContractDocument(param).subscribe(
                (result: Blob) => {
                    const fileData = result;
                    this.helperService.showBlobInBrowserTab(fileData, file.fileName);
                    this.loadingVisible = false;
                },
                (error) => {
                    this.errorService.displayError(error);
                    this.loadingVisible = false;
                }
            );
        }
    }

    onDocumentFolderClick(data: VendorContractResultDto): void {
        if (Utils.notNullAndDefined(data)) {
            this.selectedContract = data;
            this.documentTitle = 'Contract Documents';
            this.showContractDocument = true;
        }
    }

    onHistoryClick(data: VendorContractResultDto): void {
        if (Utils.notNullAndDefined(data)) {
            this.selectedContract = data;
            const param: VendorContractBaseParameterDto = new VendorContractBaseParameterDto();
            param.communityId = this.contextId;
            param.communityVendorId = this.communityVendorId;
            param.vendorContractId = this.selectedContract.vendorContractId;
            param.clearCache = true;

            this.loadingVisible = true;
            this.vendorContractService.getContractHistory(param).subscribe(
                (result: VendorContractHistoryResultDto[]) => {
                    this.contractHistoryData = result;
                    this.getHistoryColumns();
                    this.showHistory = true;
                    this.loadingVisible = false;
                },
                (error) => {
                    this.errorService.displayError(error);
                    this.loadingVisible = false;
                }
            );
        }
    }

    onAddNewContractClick(): void {
        this.selectedContract = null;
        this.showContractEditor = true;
    }

    onContractEditorClose(refresh: boolean): void {
        this.showContractEditor = false;
        if (Utils.isTrue(refresh)) {
            this.populateManageContract();
        }
    }

    onContentReady(): void {
        const selectedKeys = this.manageContracteData
            .filter((d) => Utils.notNullAndDefined(d.wfVendorContractXrefId))
            .map((r) => r.vendorContractId);
        this.selectedVendorContracts = selectedKeys;

        if (Utils.notNullOrEmpty(this.selectedVendorContracts)) {
            this.contractGrid.instance.selectRows(this.selectedVendorContracts, false);
        }
    }

    onGridSelection(event: any): void {
        const selectedKeys = this.contractGrid.instance.getSelectedRowKeys();

        this.workflowVendorContracts = [];
        selectedKeys.forEach((vendorContractId) => {
            this.workflowVendorContracts.push({
                vendorContractId,
                crudAction: ActionConstants.ADD
            });
        });

        this.selectedVendorContracts.forEach((vendorContractId) => {
            if (Utils.notInList(vendorContractId, selectedKeys)) {
                this.workflowVendorContracts.push({
                    vendorContractId,
                    crudAction: ActionConstants.DELETE
                });
            }
        });

        this.selectedVendorContracts = selectedKeys;
    }

    onSaveWFVendorContractClick(): void {
        this.onSaveWorkflowVendorContractClick.emit(this.workflowVendorContracts);
    }

    private deleteContract(deleteAllDocuments: boolean): void {
        const param: DeleteVendorContractParameterDto = new DeleteVendorContractParameterDto();
        param.deleteAllDocuments = deleteAllDocuments;
        param.communityId = this.contextId;
        param.communityVendorId = this.communityVendorId;
        param.vendorContractId = this.selectedContract.vendorContractId;
        param.clearCache = true;
        this.loadingVisible = true;
        this.vendorContractService.deleteContract(param).subscribe(
            () => {
                notify('Insurance document successfully deleted', 'success', MessageInterval.successMsgInterval);
                this.loadingVisible = false;
                this.populateManageContract();
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private populateManageContract(): void {
        const param: CommunityVendorBaseParametersDto = new CommunityVendorBaseParametersDto();
        param.communityId = this.contextId;
        param.communityVendorId = this.communityVendorId;
        param.vendorCode = this.vendorCode;
        param.clearCache = true;
        this.loadingVisible = true;
        this.vendorManagementSharedService.getManageVendorContract(param).subscribe(
            (result: ManageVendorContractResultDto) => {
                this.allowAddVendorContract = result.allowAddVendorContract;
                this.allowActions = result.allowActions;
                this.manageContracteData = result.vendorContracts;

                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private buildActionItems(): void {
        this.actionMenuItems = [
            { action: ActionConstants.EDIT, name: 'Edit', icon: this.iconEdit },
            {
                action: ActionConstants.DELETE,
                name: 'Delete',
                icon: this.iconDelete
            }
        ];
    }

    private getHistoryColumns(): void {
        this.contractHistoryColumns = [
            {
                dataType: 'string',
                dataField: 'action',
                caption: 'Action'
            },
            {
                dataType: 'string',
                dataField: 'user',
                caption: 'User',
                cellTemplate: 'userTemplate',
                alignment: 'center'
            },
            {
                dataType: 'datetime',
                dataField: 'actionedOn',
                caption: 'Date',
                format: this.dateTimeFormat
            },
            {
                dataType: 'string',
                dataField: 'additionalInformation',
                caption: 'Additional Info'
            }
        ];
    }
}
